// Wspólne stałe i helpery modułu realizacji (wnioski o przyłączenie).

export type Stage =
  | 'application' | 'analysis' | 'conditions' | 'design'
  | 'agreement' | 'fieldwork' | 'acceptance' | 'contract' | 'completed'
  | 'rejected' | 'cancelled'

export interface StageMeta {
  key: Stage
  label: string          // pełna nazwa
  short: string          // krótka (na kolumnę)
  color: string          // kolor akcentu (hex)
  bg: string             // tło nagłówka kolumny (tailwind)
  owner: 'office' | 'applicant' | 'none'  // czyja "piłka"
}

// Kolejność etapów na tablicy (pipeline + tory terminalne na końcu)
export const STAGE_ORDER: Stage[] = [
  'application', 'analysis', 'conditions', 'design',
  'agreement', 'fieldwork', 'acceptance', 'contract', 'completed',
  'rejected', 'cancelled',
]

export const STAGES: Record<Stage, StageMeta> = {
  application: { key: 'application', label: 'Wniosek', short: 'Wniosek', color: '#6b7280', bg: 'bg-gray-100', owner: 'none' },
  analysis:    { key: 'analysis',    label: 'Analiza', short: 'Analiza', color: '#2563eb', bg: 'bg-blue-100', owner: 'office' },
  conditions:  { key: 'conditions',  label: 'Warunki', short: 'Warunki', color: '#7c3aed', bg: 'bg-violet-100', owner: 'office' },
  design:      { key: 'design',      label: 'Projekt', short: 'Projekt', color: '#0891b2', bg: 'bg-cyan-100', owner: 'applicant' },
  agreement:   { key: 'agreement',   label: 'Uzgodnienie', short: 'Uzgodn.', color: '#d97706', bg: 'bg-amber-100', owner: 'office' },
  fieldwork:   { key: 'fieldwork',   label: 'Realizacja w terenie', short: 'Realizacja', color: '#ca8a04', bg: 'bg-yellow-100', owner: 'applicant' },
  acceptance:  { key: 'acceptance',  label: 'Odbiór', short: 'Odbiór', color: '#16a34a', bg: 'bg-green-100', owner: 'office' },
  contract:    { key: 'contract',    label: 'Umowa', short: 'Umowa', color: '#0d9488', bg: 'bg-teal-100', owner: 'office' },
  completed:   { key: 'completed',   label: 'Zakończone', short: 'Zakończone', color: '#15803d', bg: 'bg-green-200', owner: 'none' },
  rejected:    { key: 'rejected',    label: 'Odrzucone', short: 'Odrzucone', color: '#dc2626', bg: 'bg-red-100', owner: 'none' },
  cancelled:   { key: 'cancelled',   label: 'Anulowane', short: 'Anulowane', color: '#6b7280', bg: 'bg-gray-200', owner: 'none' },
}

export function stageMeta(stage: string | null | undefined): StageMeta {
  return STAGES[(stage as Stage)] || STAGES.application
}

// Etapy, w których "piłka" jest po stronie urzędu (skrzynka "Do mnie")
export const OFFICE_STAGES: Stage[] = ['analysis', 'conditions', 'agreement', 'acceptance', 'contract']

export interface Realization {
  id: number
  client_id: number
  client_name?: string | null
  applicant_name: string
  applicant_contact?: string | null
  location: string
  plot_number: string
  connection_water: boolean
  connection_sewer: boolean
  connection_type?: string | null
  geometry?: string | null
  suw_id?: number | null
  suw_name?: string | null
  stage: Stage
  status: string
  water_conditions?: string | null
  sewer_conditions?: string | null
  water_accepted?: boolean
  sewer_accepted?: boolean
  conditions_number?: string | null
  conditions_date?: string | null
  conditions_valid_until?: string | null
  contract_number?: string | null
  has_inventory?: boolean
  created_at?: string | null
  updated_at?: string | null
  steps?: TimelineEvent[]
}

export interface TimelineEvent {
  id: number
  step_type: string
  status: string
  data?: any
  completed_at?: string | null
  user_name?: string | null
  notes?: string | null
  created_at?: string | null
}

export interface RealizationStats {
  total: number
  active: number
  by_stage: Record<string, number>
  media: { water: number; sewer: number; both: number }
  todo_office: number
  conditions_expiring: number
  conditions_expired: number
}

// Ile dni sprawa "stoi" w bieżącym etapie (od ostatniej aktualizacji)
export function daysInStage(r: Realization): number {
  const from = r.updated_at || r.created_at
  if (!from) return 0
  const ms = Date.now() - new Date(from).getTime()
  return Math.max(0, Math.floor(ms / (24 * 3600 * 1000)))
}

// Etykiety mediów do wyświetlenia
export function mediaLabels(r: Pick<Realization, 'connection_water' | 'connection_sewer'>): string[] {
  const out: string[] = []
  if (r.connection_water) out.push('Woda')
  if (r.connection_sewer) out.push('Kanał')
  return out
}

// Dni do wygaśnięcia ważności warunków (null gdy brak daty). Ujemne = po terminie.
export function conditionsDaysLeft(r: Realization): number | null {
  if (!r.conditions_valid_until) return null
  const ms = new Date(r.conditions_valid_until).getTime() - Date.now()
  return Math.ceil(ms / (24 * 3600 * 1000))
}

export function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` }
}
